import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Layout from "../../components/layout/app-layout";
import { useAuth } from "../../hooks/use-auth";
import { useAuthenticatedRequest } from "../../hooks/use-authenticated-request";
import { useToast } from "../../hooks/use-toast";
import { User, Tenant } from "../../types";
import { 
  Table, 
  TableHeader, 
  TableBody, 
  TableHead, 
  TableRow, 
  TableCell 
} from "../../components/ui/table";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription,
  DialogFooter 
} from "../../components/ui/dialog";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "../../components/ui/select";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { UserPlus, Loader2, Search, MoreVertical, Edit, Trash2, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";

interface InviteUserForm {
  email: string;
  role: "TenantAdmin" | "Reviewer" | "SuperAdmin";
  tenantId?: string;
}

// interface EditUserForm {
//   fullName: string;
//   role: string;
//   tenantId: string;
// }

export default function UserManagementPage() {
  const { user } = useAuth();
  const authenticatedRequest = useAuthenticatedRequest();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState<InviteUserForm>({
    email: "",
    role: "Reviewer",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState<string>("all");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [userBeingEdited, setUserBeingEdited] = useState<User | null>(null);
  const [editForm, setEditForm] = useState({
    fullName: "",
    role: "Reviewer" as "Reviewer" | "TenantAdmin" | "SuperAdmin",
    tenantId: "",
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);

  // Fetch users
  const { data: users, isLoading } = useQuery<User[]>({
    queryKey: ["users", user?.isSuperAdmin ? user.activeTenantContext : user?.tenantId],
    queryFn: async () => {
      const response = await authenticatedRequest("/api/users");
      if (!response.ok) {
        throw new Error("Failed to fetch users");
      }
      return response.json();
    },
    enabled: !!user,
  });

  // Fetch tenants data (for SuperAdmin)
  const { data: tenants, isLoading: isLoadingTenants } = useQuery<Tenant[]>({
    queryKey: ["tenants"],
    queryFn: async () => {
      const response = await authenticatedRequest('/api/tenants/list-for-admin');
      if (!response.ok) {
        throw new Error("Failed to fetch tenants");
      }
      return response.json();
    },
    enabled: !!user?.isSuperAdmin,
  });

  // Mutation for inviting a user
  const inviteUserMutation = useMutation({
    mutationFn: async (formData: InviteUserForm) => {
      const payload = {
        email: formData.email,
        role: formData.role,
        tenantId: formData.role === "SuperAdmin" 
          ? null 
          : (formData.tenantId || user?.tenantId),
      };
      
      // Double-check that tenantId is null for SuperAdmin
      if (formData.role === "SuperAdmin" && payload.tenantId !== null) {
        console.log("Forcing tenantId to null for SuperAdmin");
        payload.tenantId = null;
      }

      console.log("Sending invite with payload:", JSON.stringify(payload));

      const response = await authenticatedRequest("/api/users/invite", {
        method: "POST",
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: "Failed to invite user" }));
        throw new Error(errorData.message || "Failed to invite user");
      }

      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "User invitation sent successfully",
      });
      setIsInviteDialogOpen(false);
      setInviteForm({ email: "", role: "Reviewer" });
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (error: Error) => {
      console.error("Invitation error:", error);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Mutation for editing a user
  const editUserMutation = useMutation({
    mutationFn: async ({ userId, fullName, role, tenantId }: { userId: string; fullName: string; role: string; tenantId?: string }) => {
      const payload: { fullName: string; role: string; tenantId?: string } = { fullName, role };
      if (role !== "SuperAdmin") payload.tenantId = tenantId;
      
      const response = await authenticatedRequest(`/api/users/${userId}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update user");
      }
      return response.json();
    },
    onSuccess: () => {
      toast({ title: "Success", description: "User updated successfully" });
      setEditDialogOpen(false);
      setUserBeingEdited(null);
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  // Mutation for deleting a user
  const deleteUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      const response = await authenticatedRequest(`/api/users/${userId}`, {
        method: "DELETE",
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete user");
      }
      
      return response.json();
    },
    onSuccess: () => {
      toast({ 
        title: "Success", 
        description: "User deleted successfully" 
      });
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (error: Error) => {
      toast({ 
        title: "Error", 
        description: error.message, 
        variant: "destructive" 
      });
    },
  });

  const handleInviteSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    inviteUserMutation.mutate(inviteForm);
  };

  const openEditDialog = (userData: User) => {
    setUserBeingEdited(userData);
    setEditForm({
      fullName: userData.fullName || "",
      role: userData.role,
      tenantId: userData.tenantId || "",
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!userBeingEdited) return;
    editUserMutation.mutate({
      userId: userBeingEdited.userId,
      fullName: editForm.fullName,
      role: editForm.role,
      tenantId: editForm.role !== "SuperAdmin" ? editForm.tenantId : undefined,
    });
  };

  const openDeleteDialog = (userData: User) => {
    setUserToDelete(userData);
    setDeleteDialogOpen(true);
  };

  const handleDeleteUser = () => {
    if (!userToDelete) return;
    deleteUserMutation.mutate(userToDelete.userId);
  };

  // Filtered and searched users
  const filteredUsers = useMemo(() => {
    if (!users) return [];
    return users.filter((userData) => {
      const searchMatch =
        (userData.email && userData.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (userData.fullName &&
          userData.fullName.toLowerCase().includes(searchTerm.toLowerCase()));
      const roleMatch = roleFilter === "all" || userData.role === roleFilter;
      
      return searchMatch && roleMatch;
    });
  }, [users, searchTerm, roleFilter]);

  // Get tenant name by ID
  const getTenantName = (tenantId: string, userRole: string) => {
    // For SuperAdmins, show 'System-wide' instead of the actual tenant
    if (userRole === 'SuperAdmin') {
      return "System-wide";
    }
    
    const tenant = tenants?.find(t => t.tenantId === tenantId);
    return tenant?.companyName || tenantId;
  };

  // Get role badge variant
  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'SuperAdmin':
        return "destructive";
      case 'TenantAdmin':
        return "secondary";
      default:
        return "default";
    }
  };

  if (!user) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <p>Please log in to access user management.</p>
        </div>
      </Layout>
    );
  }

  if (!user.isSuperAdmin && user.role !== "TenantAdmin") {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <p>You don't have permission to access user management.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">User Management</h1>
          <div className="flex space-x-2">
            <Button 
              onClick={() => setIsInviteDialogOpen(true)}
            >
              <UserPlus className="mr-2 h-4 w-4" />
              Invite User
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>All Users</CardTitle>
            {/* Add Filters and Search Bar */}
            <div className="mt-4 flex flex-col sm:flex-row gap-4">
              <div className="relative sm:w-1/2">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Search by email or name..."
                  className="pl-8 w-full"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="sm:w-[180px]">
                  <SelectValue placeholder="Filter by Role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="SuperAdmin">Super Admin</SelectItem>
                  <SelectItem value="TenantAdmin">Tenant Admin</SelectItem>
                  <SelectItem value="Reviewer">Reviewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center items-center h-64">
                <Loader2 className="h-8 w-8 animate-spin" />
                <span className="ml-2">Loading users...</span>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Tenant</TableHead>
                    <TableHead>Created At</TableHead>
                    <TableHead>Last Active</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers && filteredUsers.length > 0 ? (
                    filteredUsers.map((userData) => (
                      <TableRow key={userData.userId}>
                        <TableCell>
                          <div className="font-medium">{userData.fullName || "N/A"}</div>
                          <div className="text-sm text-muted-foreground">
                            {userData.email}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={getRoleBadgeVariant(userData.role)}>
                            {userData.role}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-muted/50">
                            {userData.tenantId ? getTenantName(userData.tenantId, userData.role) : "N/A"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {userData.createdAt ? new Date(userData.createdAt).toLocaleDateString() : "N/A"}
                        </TableCell>
                        <TableCell>
                          {userData.lastSignInAt ? new Date(userData.lastSignInAt).toLocaleString() : "N/A"}
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8 p-0">
                                <MoreVertical className="h-4 w-4" />
                                <span className="sr-only">Open menu</span>
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuLabel>Actions</DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => openEditDialog(userData)}>
                                <Edit className="mr-2 h-4 w-4" />
                                Edit User
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                className="text-destructive" 
                                disabled={userData.userId === user.userId}
                                onClick={() => openDeleteDialog(userData)}
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete User
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8">
                        No users match your filters.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Edit User Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit User</DialogTitle>
              <DialogDescription>
                Update user details and permissions.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-fullName">Full Name</Label>
                <Input
                  id="edit-fullName"
                  type="text"
                  value={editForm.fullName}
                  onChange={(e) => setEditForm({ ...editForm, fullName: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-role">Role</Label>
                <Select
                  value={editForm.role}
                  onValueChange={(value) => setEditForm({ ...editForm, role: value as "Reviewer" | "TenantAdmin" | "SuperAdmin" })}
                  disabled={!user.isSuperAdmin}
                >
                  <SelectTrigger id="edit-role">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Reviewer">Reviewer</SelectItem>
                    <SelectItem value="TenantAdmin">Tenant Admin</SelectItem>
                    <SelectItem value="SuperAdmin">Super Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {user.isSuperAdmin && editForm.role !== "SuperAdmin" && (
                <div className="space-y-2">
                  <Label htmlFor="edit-tenant">Tenant</Label>
                  <Select
                    value={editForm.tenantId}
                    onValueChange={(value) => setEditForm({ ...editForm, tenantId: value })}
                    required
                  >
                    <SelectTrigger id="edit-tenant">
                      <SelectValue placeholder="Select tenant" />
                    </SelectTrigger>
                    <SelectContent>
                      {isLoadingTenants ? (
                        <div className="flex items-center justify-center p-2">
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          Loading...
                        </div>
                      ) : (
                        tenants?.filter(tenant => tenant.tenantId !== "00000000-0000-0000-0000-000000000000")
                          .map((tenant) => (
                            <SelectItem key={tenant.tenantId} value={tenant.tenantId}>
                              {tenant.companyName}
                            </SelectItem>
                          ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={editUserMutation.isPending}>
                  {editUserMutation.isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Save Changes
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Invite User Dialog */}
        <Dialog open={isInviteDialogOpen} onOpenChange={setIsInviteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Invite New User</DialogTitle>
              <DialogDescription>
                Send an invitation email to add a new user to the system.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleInviteSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  placeholder="user@example.com"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Select
                  value={inviteForm.role}
                  onValueChange={(value) => 
                    setInviteForm({ 
                      ...inviteForm, 
                      role: value as "TenantAdmin" | "Reviewer" | "SuperAdmin" 
                    })
                  }
                >
                  <SelectTrigger id="role">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Reviewer">Reviewer</SelectItem>
                    <SelectItem value="TenantAdmin">Tenant Admin</SelectItem>
                    {user.isSuperAdmin && (
                      <SelectItem value="SuperAdmin">Super Admin</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>

              {user.isSuperAdmin && inviteForm.role !== "SuperAdmin" && (
                <div className="space-y-2">
                  <Label htmlFor="tenant">Tenant</Label>
                  <Select
                    value={inviteForm.tenantId}
                    onValueChange={(value) => setInviteForm({ ...inviteForm, tenantId: value })}
                    required
                  >
                    <SelectTrigger id="tenant">
                      <SelectValue placeholder="Select tenant" />
                    </SelectTrigger>
                    <SelectContent>
                      {isLoadingTenants ? (
                        <div className="flex items-center justify-center p-2">
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          Loading...
                        </div>
                      ) : (
                        tenants?.filter(tenant => tenant.tenantId !== "00000000-0000-0000-0000-000000000000")
                          .map((tenant) => (
                            <SelectItem key={tenant.tenantId} value={tenant.tenantId}>
                              {tenant.companyName}
                            </SelectItem>
                          ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <DialogFooter>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setIsInviteDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={inviteUserMutation.isPending}
                >
                  {inviteUserMutation.isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Send Invitation
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete User Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center">
                <AlertTriangle className="h-5 w-5 text-destructive mr-2" />
                Confirm Deletion
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete the user {userToDelete?.fullName || userToDelete?.email}? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex space-x-2 sm:justify-end pt-4">
              <Button type="button" variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={handleDeleteUser}
                disabled={deleteUserMutation.isPending}
              >
                {deleteUserMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Delete User
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
} 